/**
 * WorkflowResultsContext — Results slice of the Athena workflow.
 *
 * Owns the entire `sessionData` namespace: the per-agent result objects
 * (itemDefinition, documents, assets, threats, damageScenarios,
 * attackPaths, risks, cybersecurityGoals, cybersecurityClaims,
 * cybersecurityControls, cybersecurityRequirements, requirements,
 * report) plus runtime fields populated during restoration
 * (residualTARA, residualControls, riskManagementMode, isRestored,
 * restorationData, session_status, current_agent, completed_agents,
 * agent_awaiting_review, ...).
 *
 * Hierarchical TARA fields (analysis_mode, tara_level, parent_sessions)
 * temporarily live nested in `sessionData` here for backwards
 * compatibility — Story 4.5 splits them out into Session top-level.
 *
 * The per-agent restoration switch (lines 880-1280 of
 * WorkflowMachineContext.jsx, inside `restoreInProgressSession`) is NOT
 * moved into this context — it stays in the god-context residue and
 * dispatches into here via the dispatch bridge in
 * WorkflowMachineProviderInner.
 *
 * Hook surface mirrors 4.2/4.3 (flat object: state spread +
 * action-creators, NOT { state, dispatch }).
 *
 * Refs:
 * - Modularity Epic 4 plan §4.3 (revised after Story 4.4 audit)
 * - docs/ATHENA_WORKFLOW_CONTEXT_SLICES.md §1 (Results row)
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

const WorkflowResultsContext = createContext(null);

export const RESULTS_ACTIONS = {
  UPDATE_SESSION_DATA: 'UPDATE_SESSION_DATA',
  SET_DOCUMENTS: 'SET_DOCUMENTS',
  SET_ITEM_DEFINITION: 'SET_ITEM_DEFINITION',
  SET_ASSETS: 'SET_ASSETS',
  SET_THREATS: 'SET_THREATS',
  SET_DAMAGE_SCENARIOS: 'SET_DAMAGE_SCENARIOS',
  SET_ATTACK_PATHS: 'SET_ATTACK_PATHS',
  SET_RISKS: 'SET_RISKS',
  SET_CYBERSECURITY_GOALS: 'SET_CYBERSECURITY_GOALS',
  SET_CYBERSECURITY_CLAIMS: 'SET_CYBERSECURITY_CLAIMS',
  SET_CYBERSECURITY_CONTROLS: 'SET_CYBERSECURITY_CONTROLS',
  SET_REQUIREMENTS: 'SET_REQUIREMENTS',
  SET_REPORT: 'SET_REPORT',
};

export const initialResultsState = {
  sessionData: {
    itemDefinition: {},
    documents: [],
    assets: [],
    threats: [],
    damageScenarios: [],
    attackPaths: [],
    risks: [],
    cybersecurityGoals: [],
    cybersecurityClaims: [],
    cybersecurityControls: [],
    cybersecurityRequirements: [],
    requirements: [],
    // Note: SET_REQUIREMENTS writes to `requirements`;
    // setCybersecurityRequirements writes to `cybersecurityRequirements`
    // via UPDATE_SESSION_DATA. Two distinct fields preserved verbatim.
    report: null,
    // Hierarchical TARA fields — temporarily here until Story 4.5
    analysis_mode: 'individual',
    tara_level: 'unassigned',
    parent_sessions: [],
  },
};

export function resultsReducer(state, action) {
  switch (action.type) {
    case RESULTS_ACTIONS.UPDATE_SESSION_DATA:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          ...action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_DOCUMENTS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          documents: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_ITEM_DEFINITION:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          itemDefinition: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_ASSETS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          assets: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_THREATS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          threats: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_DAMAGE_SCENARIOS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          damageScenarios: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_ATTACK_PATHS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          attackPaths: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_RISKS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          risks: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_CYBERSECURITY_GOALS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          cybersecurityGoals: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_CYBERSECURITY_CLAIMS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          cybersecurityClaims: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_CYBERSECURITY_CONTROLS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          cybersecurityControls: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_REQUIREMENTS:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          requirements: action.payload,
        },
      };

    case RESULTS_ACTIONS.SET_REPORT:
      return {
        ...state,
        sessionData: {
          ...state.sessionData,
          report: action.payload,
        },
      };

    default:
      return state;
  }
}

export function WorkflowResultsProvider({ children }) {
  const [state, dispatch] = useReducer(resultsReducer, initialResultsState);

  // Action-creators (preserve byte-equivalent behaviour with the originals
  // in WorkflowMachineContext.jsx).

  const updateSessionData = useCallback((updates) => {
    dispatch({ type: RESULTS_ACTIONS.UPDATE_SESSION_DATA, payload: updates });
  }, []);

  const setDocuments = useCallback((documents) => {
    dispatch({ type: RESULTS_ACTIONS.SET_DOCUMENTS, payload: documents });
  }, []);

  const setItemDefinition = useCallback((itemDefinition) => {
    dispatch({ type: RESULTS_ACTIONS.SET_ITEM_DEFINITION, payload: itemDefinition });
  }, []);

  const setAssets = useCallback((assets) => {
    dispatch({ type: RESULTS_ACTIONS.SET_ASSETS, payload: assets });
  }, []);

  const setThreats = useCallback((threats) => {
    dispatch({ type: RESULTS_ACTIONS.SET_THREATS, payload: threats });
  }, []);

  const setDamageScenarios = useCallback((damageScenarios) => {
    dispatch({ type: RESULTS_ACTIONS.SET_DAMAGE_SCENARIOS, payload: damageScenarios });
  }, []);

  const setAttackPaths = useCallback((attackPaths) => {
    dispatch({ type: RESULTS_ACTIONS.SET_ATTACK_PATHS, payload: attackPaths });
  }, []);

  const setRisks = useCallback((risks) => {
    dispatch({ type: RESULTS_ACTIONS.SET_RISKS, payload: risks });
  }, []);

  const setCybersecurityGoals = useCallback((goals) => {
    dispatch({ type: RESULTS_ACTIONS.SET_CYBERSECURITY_GOALS, payload: goals });
  }, []);

  const setCybersecurityClaims = useCallback((claims) => {
    dispatch({ type: RESULTS_ACTIONS.SET_CYBERSECURITY_CLAIMS, payload: claims });
  }, []);

  const setCybersecurityControls = useCallback((controls) => {
    dispatch({ type: RESULTS_ACTIONS.SET_CYBERSECURITY_CONTROLS, payload: controls });
  }, []);

  // Preserved byte-equivalent to the original god-context: this writes
  // to `sessionData.cybersecurityRequirements` (a distinct field from
  // `sessionData.requirements`) via UPDATE_SESSION_DATA.
  const setCybersecurityRequirements = useCallback((requirements) => {
    dispatch({
      type: RESULTS_ACTIONS.UPDATE_SESSION_DATA,
      payload: { cybersecurityRequirements: requirements },
    });
  }, []);

  const setRequirements = useCallback((requirements) => {
    dispatch({ type: RESULTS_ACTIONS.SET_REQUIREMENTS, payload: requirements });
  }, []);

  const setReport = useCallback((report) => {
    dispatch({ type: RESULTS_ACTIONS.SET_REPORT, payload: report });
  }, []);

  const contextValue = useMemo(() => ({
    // State
    ...state,

    // Action-creators (13 + 1 alias for setCybersecurityRequirements)
    updateSessionData,
    setDocuments,
    setItemDefinition,
    setAssets,
    setThreats,
    setDamageScenarios,
    setAttackPaths,
    setRisks,
    setCybersecurityGoals,
    setCybersecurityClaims,
    setCybersecurityControls,
    setCybersecurityRequirements,
    setRequirements,
    setReport,
  }), [
    state,
    updateSessionData,
    setDocuments,
    setItemDefinition,
    setAssets,
    setThreats,
    setDamageScenarios,
    setAttackPaths,
    setRisks,
    setCybersecurityGoals,
    setCybersecurityClaims,
    setCybersecurityControls,
    setCybersecurityRequirements,
    setRequirements,
    setReport,
  ]);

  return (
    <WorkflowResultsContext.Provider value={contextValue}>
      {children}
    </WorkflowResultsContext.Provider>
  );
}

export function useWorkflowResults() {
  const context = useContext(WorkflowResultsContext);

  if (!context) {
    throw new Error('useWorkflowResults must be used within a WorkflowResultsProvider');
  }

  return context;
}
